<template>
    <div>
        <!-- 两格的分数 -->
        <div class="flex-col" v-if="roundIndex != 9 || !isOldScore">
            <b :class="['scoreNumber', { 'scoreNumber-old': isOldScore }]">{{ roundIndex + 1 }}</b>
            <div>
                <table border="0" cellspacing="0" cellpadding="0">
                    <tr>
                        <td :class="['score1', { 'score1-old': isOldScore }]" @click="onClickScore(0)">
                            <b :class="scoreColorClass(score1Modified)">{{ score1 }}</b>
                        </td>
                        <td :class="['score2', { 'score2-old': isOldScore }]" @click="onClickScore(1)">
                            <b :class="scoreColorClass(score2Modified)">{{ score2Display }}</b>
                        </td>
                    </tr>
                </table>
            </div>
            <div>
                <table border="0" cellspacing="0" cellpadding="0">
                    <tr>
                        <td :class="['roundScore', { 'roundScore-old': isOldScore }]">
                            <b>{{ roundTotalScore }}</b>
                        </td>
                    </tr>
                </table>
            </div>
            <div>
                <table class="pinState" border="0" cellspacing="0" cellpadding="0">
                    <tr>
                        <td>
                            <v-pin-state :w="isOldScore ? '0.6rem' : '0.7rem'" :h="isOldScore ? '0.6rem' : '0.7rem'"
                                :show-replay="showReplay" :pins1="roundScore.pins1"
                                :pins2="roundScore.pins2"></v-pin-state>
                        </td>
                    </tr>
                </table>
            </div>
        </div>
        <!-- 三格的分数 -->
        <div class="flex-col" v-if="roundIndex == 9 && isOldScore">
            <b :class="['scoreNumber', { 'scoreNumber-old': isOldScore }]">{{ roundIndex + 1 }}</b>
            <div>
                <table border="0" cellspacing="0" cellpadding="0">
                    <tr>
                        <td :class="['lastScore1', { 'lastScore1-old': isOldScore }]">
                            <b :class="scoreColorClass(score1Modified)">{{ score1 }}</b>
                        </td>
                        <td :class="['lastScore2', { 'lastScore2-old': isOldScore }]">
                            <b :class="scoreColorClass(score2Modified)">{{ score2Display }}</b>
                        </td>
                        <td :class="['lastScore1', { 'lastScore1-old': isOldScore }]">
                            <b :class="scoreColorClass(score3Modified)">{{ score3Display }}</b>
                        </td>
                    </tr>
                </table>
            </div>
            <div>
                <table border="0" cellspacing="0" cellpadding="0">
                    <tr>
                        <td :class="['roundScore2', { 'roundScore2-old': isOldScore }]">
                            <b>{{ roundTotalScore }}</b>
                        </td>
                    </tr>
                </table>
            </div>
            <div>
                <table border="0" cellspacing="0" cellpadding="0">
                    <tr>
                        <td>
                            <v-pin-state :w="isOldScore ? '0.6rem' : '0.7rem'" :h="isOldScore ? '0.6rem' : '0.7rem'"
                                :show-replay="showReplay" :pins1="roundScore.pins1"></v-pin-state>
                        </td>
                        <td>
                            <v-pin-state :w="isOldScore ? '0.6rem' : '0.7rem'" :h="isOldScore ? '0.6rem' : '0.7rem'"
                                :show-replay="showReplay" :pins1="roundScore.pins2"></v-pin-state>
                        </td>
                        <td>
                            <v-pin-state :w="isOldScore ? '0.6rem' : '0.7rem'" :h="isOldScore ? '0.6rem' : '0.7rem'"
                                :show-replay="showReplay" :pins1="roundScore.pins3"></v-pin-state>
                        </td>
                    </tr>
                </table>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import vPinState from './PinState.vue';
import jsUtil from '@/global/jsUtil';

const props = defineProps({
    roundScore: {
        type: Object,
        default: () => ({
            score1: 0,
            score2: 0,
            score3: 0,
            totalScore: 0,
            score1Modified: false,
            score2Modified: false,
            score3Modified: false,
            pins1: Array(10).fill(true),
            pins2: Array(10).fill(false),
            pins3: Array(10).fill(true)
        })
    },
    index: {
        type: Number,
        default: 9
    },
    isOldScore: {
        type: Boolean,
        default: true
    },
    showReplay: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['score-click']); // 显式定义emit
const roundIndex = computed(() => props.index);
const score1 = computed(() => props.roundScore.score1);
const score1Modified = computed(() => props.roundScore.score1Moidified);
const score2 = computed(() => props.roundScore.score2);
const score2Modified = computed(() => props.roundScore.score2Moidified);
const score3 = computed(() => props.roundScore.score3);
const score3Modified = computed(() => props.roundScore.score3Moidified);
const roundTotalScore = computed(() => props.roundScore.totalScore);

const score2Display = computed(() => {
    const s1 = score1.value;
    const s2 = score2.value;
    return scoreDisplay(s1, s2);
});

const score3Display = computed(() => {
    const s1 = score2.value;
    const s2 = score3.value;
    return scoreDisplay(s1, s2);
});

const scoreColorClass = computed(() => (isModified) => {
    return isModified ? 'modifiedScore' : '';
});

const scoreDisplay = (s1, s2) => {
    if (!jsUtil.hasValue(s1) || jsUtil.isBlank(s1)) {
        return s2;
    }

    if (jsUtil.hasValue(s2) && !jsUtil.isBlank(s2)) {
        if (is10(s1)) {
            return prevIs10(s2);
        } else {
            return preNot10(s1, s2);
        }
    } else {
        return s2;
    }
};

const getScoreFromStr = (s) => {
    if (isF(s)) {
        return 0;
    }
    if (is10(s)) {
        return 10;
    }
    return s;
};

const is10 = (s) => s === '10' || s === 'X' || s === '/';
const isF = (s) => s === 'F';

const preNot10 = (s1, s2) => {
    if (is10(s2)) {
        return '/';
    }
    if (isF(s2)) {
        return s2;
    }
    const s = getScoreFromStr(s2) - getScoreFromStr(s1);
    return s < 0 ? 0 : s;
};

const prevIs10 = (s) => {
    if (is10(s)) {
        return 'X';
    }
    if (isF(s)) {
        return s;
    }
    return s;
};

const onClickScore = (scoreIndex) => {
    emit('score-click', roundIndex.value, scoreIndex);
};
</script>

<style>
.scoreNumber {
    font-size: 18px;
}

.scoreNumber-old {
    font-size: 14px !important;
}

.modifiedScore {
    color: gold;
}

.score1 {
    background-color: gray;
    color: white;
    text-align: center;
    width: 0.4rem;
    height: 0.4rem;
    font-size: 0.35rem;
    font-weight: bold;
}

.lastScore1 {
    background-color: gray;
    color: white;
    text-align: center;
    width: 0.8rem;
    height: 0.4rem;
    font-size: 0.35rem;
    font-weight: bold;
}

.score2 {
    background-color: teal;
    color: white;
    text-align: center;
    width: 0.4rem;
    height: 0.4rem;
    font-size: 0.35rem;
    font-weight: bold;
}

.lastScore2 {
    background-color: teal;
    color: white;
    text-align: center;
    width: 0.8rem;
    height: 0.4rem;
    font-size: 0.35rem;
    font-weight: bold;
}

.roundScore {
    background-color: gray;
    color: white;
    text-align: center;
    width: 0.8rem;
    height: 0.4rem;
    font-weight: bold;
    font-size: 0.35rem;
}

.roundScore2 {
    background-color: gray;
    color: white;
    text-align: center;
    width: 2.4rem;
    height: 0.4rem;
    font-weight: bold;
    font-size: 0.35rem;
}

.scoreIndex {
    background-color: lightgray;
    font-size: 1.2rem;
    height: 1.2rem;
    text-align: center;
}

.score1-old {
    width: 0.3rem !important;
    height: 0.3rem !important;
    font-size: 0.25rem !important;
}

.score2-old {
    width: 0.3rem !important;
    height: 0.3rem !important;
    font-size: 0.25rem !important;
}

.lastScore1-old {
    width: 0.7rem !important;
    height: 0.3rem !important;
    font-size: 0.25rem !important;
}

.lastScore2-old {
    width: 0.7rem !important;
    height: 0.3rem !important;
    font-size: 0.25rem !important;
}

.roundScore-old {
    width: 0.7rem !important;
    height: 0.3rem !important;
    font-size: 0.25rem !important;
}

.roundScore2-old {
    width: 2.3rem !important;
    height: 0.3rem !important;
    font-size: 0.25rem !important;
}
</style>